import TimeSince from "components/TimeSince/TimeSince";
import { __callbacks } from "Utils/Callbacks/Callback";
import { ChatMessage } from "Utils/Chats/ChatMessage";
import { ChatMessageFragment } from "Utils/Chats/ChatMessageFragment";
import { MsgFragmentNewChild } from "Utils/Chats/MsgFragmentNewChild";
import { __debug } from "Utils/Debug/Debug";
import { Chat } from "Utils/Enums";
import { IMsgFragmentNewChild } from "Utils/Interfaces";
import { __utils } from "Utils/Utils";
import React from "react";
import { useCallback, useEffect, useState } from "react";
import Image from "../Image/Image";
import Navigation from "../Navigation/Navigation";
import Quiz from "../Quiz/Quiz";
import Text from "../Text/Text";
import styles from "./MessageReceive.module.scss";
import { AvatarComponent } from 'avatar-initials';
import { MessageNumber } from "components/ChatApp/components/Body/components/Utils/MessageNumber";
import { __bot } from "../../../../../../Utils/Bot/Bot";

const components = {
    Text,
    Quiz,
    Image,
    Navigation
};

const botIcon: string = "./assets/icons/bot.svg";

export default function MessageReceive(props: any) {

    const [time] = useState(props.time ?? Date.now());
    const [id] = useState(props.id ?? __utils.getUuidv4());
    const [classGroup, setClassGroup] = useState("");
    const [childs, setChilds] = useState<any[]>(props.children);
    const [fragmentNo] = useState(new MessageNumber());

    // Ensure userName is correctly assigned
    const [userId] = useState<string>(props.userId);
    const [userName] = useState<string>(props.userName === "bot" ? __bot.botName : props.userName);
    const [userPhoto] = useState<string>(props.userPhoto || botIcon);
    const [userProps] = useState({
        userId,
        userName,
        userPhoto
    });


    const getNewFragmentNo = useCallback(
        () => {
            const fragNo = fragmentNo.get();
            fragmentNo.increment();
            return fragNo;
        },
        [fragmentNo]
    );

    useEffect(() => {
        if (props.local) {
            fragmentNo.set(props.children.length + 1);
        }
    }, [props, fragmentNo]);

    const initListenersEx = useCallback(
        () => {
            __callbacks.onMsgFragmentEx((msgFragmentNewChild: MsgFragmentNewChild) => {
                const newChild: IMsgFragmentNewChild = msgFragmentNewChild.get();

                if (newChild.messageType !== "MessageReceive") {
                    __debug.warn(() => {
                        console.warn("Invalid messageType in onMsgFragmentEx callback. Expected 'MessageReceive'.");
                    });
                    return __callbacks.msgFragmentEx.destroyAndCallEmpty();
                }

                if (newChild.userId !== userId) {
                    __debug.warn(() => {
                        console.warn("Invalid userId in the fragment! New Message started.");
                    });
                    return __callbacks.msgFragmentEx.destroyAndCallEmpty();
                }

                const fragmentNo = getNewFragmentNo();

                const newChildComponent = React.createElement((components as any)[newChild.fragmentType], { ...newChild.props, ...props, fragmentNo: fragmentNo });
                setChilds(arr => [...arr, newChildComponent]);

                __callbacks.messageReceive.trigger(
                    {
                        type: Chat.Fragment,
                        data: new ChatMessageFragment({
                            id: newChild.props.id ?? __utils.getUuidv4(),
                            chatId: props.conversationId,
                            messageNo: props.messageNo,
                            messageType: "MessageReceive",
                            fragmentType: newChild.fragmentType,
                            time: Date.now(),
                            props: {
                                ...newChild.props,
                                ...userProps
                            }
                        })
                    }
                );
            }, props.messageNo);
        },
        [props, getNewFragmentNo, userId, userProps]
    );

    const initListeners = useCallback(
        () => {
            if (props.local) return;

            __callbacks.messageReceive.trigger(
                {
                    type: Chat.Message,
                    data: new ChatMessage({
                        id: id,
                        chatId: props.conversationId,
                        messageNo: props.messageNo,
                        time: Date.now(),
                        messageType: "MessageReceive",
                        props: {
                            ...userProps
                        },
                        fragments: [
                            new ChatMessageFragment({
                                id: props.children[0].props.id ?? __utils.getUuidv4(),
                                chatId: props.conversationId,
                                messageNo: props.messageNo,
                                messageType: "MessageReceive",
                                fragmentType: props.children[0].props.type,
                                time: Date.now(),
                                props: {
                                    ...props.children[0].props,
                                    ...userProps
                                }
                            })
                        ]
                    })
                }
            );

            __callbacks.onMessageFragment((data: any) => {
                const { newChild } = data;

                if (newChild.messageType !== "MessageReceive") {
                    __debug.warn(() => {
                        console.warn("Invalid messageType in onMessageFragment callback. Expected 'MessageReceive'.");
                    });
                    return __callbacks.messageFragment.destroyAndCallEmpty();
                }

                const newChildComponent = React.createElement((components as any)[newChild.fragmentType], { ...data.newChild.props });
                setChilds(arr => [...arr, newChildComponent]);

                __callbacks.messageReceive.trigger(
                    {
                        type: Chat.Fragment,
                        data: new ChatMessageFragment({
                            id: newChild.props.id ?? __utils.getUuidv4(),
                            chatId: props.conversationId,
                            messageNo: props.messageNo,
                            messageType: "MessageReceive",
                            fragmentType: newChild.fragmentType,
                            time: Date.now(),
                            props: {
                                ...newChild.props,
                                ...userProps
                            }
                        })
                    }
                );
            });
        },
        [props, getNewFragmentNo, id, userProps]
    );

    useEffect(() => {
        initListeners();
        initListenersEx();
    }, [initListeners, initListenersEx]);

    useEffect(() => {
        if (childs.length > 1) setClassGroup(styles.group);
    }, [childs]);

    useEffect(() => {
        var delay = 0;
        if (props.local) return;
        __callbacks.scrollToBottomChat.trigger({ delay });
    }, [childs, props.local]);

    return (
        <div className={styles.container}>
            {
                childs.map((child, index) => {

                    // Transform the text to add links
                    const transformedText = __utils.detectAndTranslateLinks(child.props.text);

                    return (
                        <div key={index} className={`${styles["container-msg"]} ${classGroup}`}>
                            <span className={styles["user"]} title={`${userName} (${userId})`}>
                                {(userName === __bot.botName) ? (
                                    <img src={userPhoto} alt="" srcSet="" />
                                ) : (
                                    <AvatarComponent
                                        classes="rounded-full"
                                        useGravatar={false}
                                        size={44}
                                        color="#FFFFFF"
                                        background="#A13808"
                                        fontSize={16}
                                        fontWeight={400}
                                        offsetY={24}
                                        initials={`${userName[0]}`}
                                    />
                                )}
                            </span>
                            {child.props.text ? (
                                <span
                                    className={`${styles["msg-fragment"]} config-msg-receive-fragment`}
                                    dangerouslySetInnerHTML={{ __html: transformedText }}
                                ></span>
                            ) : (
                                <span className={`${styles["msg-fragment"]} config-msg-receive-fragment`}>{child}</span>
                            )}
                        </div>
                    );
                })
            }
            <div key={childs.length} className={styles["container-status"]}>
                <span>{userName}:</span>&nbsp;<span><TimeSince time={time}></TimeSince></span>
            </div>
        </div>
    );
}
